// Mirrors backend-nest/src/calendar/business-hours.ts: same weekday keys and
// HH:mm grammar, so what the form saves is exactly what the calendar tools read.
export type BusinessDayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface BusinessHoursWindow {
  day: BusinessDayKey;
  start: string;
  end: string;
}

export interface BusinessHours {
  enabled: boolean;
  timezone: string | null;
  windows: BusinessHoursWindow[];
  blackoutDates: string[];
}

export interface BusinessHoursFormState {
  enabled: boolean;
  days: BusinessDayKey[];
  start: string;
  end: string;
  blackoutDates: string[];
  // The windows as stored, so a save that does not touch the hours (toggling
  // enabled, editing blackout dates) can send them back verbatim.
  sourceWindows: BusinessHoursWindow[];
  // True when every day has exactly one window and all share the same
  // start/end: the only shape the single start/end editor can represent.
  isUniform: boolean;
}

export type BusinessHoursProblem = 'noDays' | 'endBeforeStart' | 'perDayHours';

export const BUSINESS_DAYS: ReadonlyArray<{ key: BusinessDayKey; labelKey: string }> = [
  { key: 'mon', labelKey: 'settings.businessHours.days.mon' },
  { key: 'tue', labelKey: 'settings.businessHours.days.tue' },
  { key: 'wed', labelKey: 'settings.businessHours.days.wed' },
  { key: 'thu', labelKey: 'settings.businessHours.days.thu' },
  { key: 'fri', labelKey: 'settings.businessHours.days.fri' },
  { key: 'sat', labelKey: 'settings.businessHours.days.sat' },
  { key: 'sun', labelKey: 'settings.businessHours.days.sun' },
];

const WEEKDAYS: BusinessDayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
const DEFAULT_START = '09:00';
const DEFAULT_END = '18:00';

export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  enabled: false,
  timezone: null,
  windows: WEEKDAYS.map((day) => ({ day, start: DEFAULT_START, end: DEFAULT_END })),
  blackoutDates: [],
};

// The form edits one shared start/end for the selected days; the stored shape
// stays per-day so the API contract is unchanged and a per-day editor can come
// later without a migration of stored settings.
export function toFormState(config: BusinessHours | undefined | null): BusinessHoursFormState {
  const sourceWindows = config?.windows ?? [];
  const displayed = sourceWindows.length > 0 ? sourceWindows : DEFAULT_BUSINESS_HOURS.windows;
  return {
    enabled: config?.enabled ?? false,
    ...deriveHours(displayed),
    blackoutDates: [...(config?.blackoutDates ?? [])],
    sourceWindows: sourceWindows.map((w) => ({ ...w })),
    isUniform: isUniformWindows(sourceWindows),
  };
}

export function isUniformWindows(windows: readonly BusinessHoursWindow[]): boolean {
  if (windows.length === 0) return true;
  const [first] = windows;
  const days = new Set(windows.map((w) => w.day));
  return (
    days.size === windows.length &&
    windows.every((w) => w.start === first.start && w.end === first.end)
  );
}

function deriveHours(windows: readonly BusinessHoursWindow[]) {
  return {
    days: BUSINESS_DAYS.map((d) => d.key).filter((key) => windows.some((w) => w.day === key)),
    start: windows[0]?.start ?? DEFAULT_START,
    end: windows[0]?.end ?? DEFAULT_END,
  };
}

export function hasHoursChanged(state: BusinessHoursFormState): boolean {
  if (state.sourceWindows.length === 0) return true;
  const original = deriveHours(state.sourceWindows);
  return (
    state.start !== original.start ||
    state.end !== original.end ||
    state.days.length !== original.days.length ||
    state.days.some((day) => !original.days.includes(day))
  );
}

// The windows are shown to the user as organization-local times, so the saved
// config must name that zone; null (no zone chosen yet) keeps the backend
// fallback to the zone of the call.
export function buildBusinessHours(
  state: BusinessHoursFormState,
  organizationTimezone: string | null,
): BusinessHours {
  const timezone = organizationTimezone?.trim();
  return {
    enabled: state.enabled,
    timezone: timezone ? timezone : null,
    windows: buildWindows(state),
    blackoutDates: [...state.blackoutDates].sort(),
  };
}

function buildWindows(state: BusinessHoursFormState): BusinessHoursWindow[] {
  if (!hasHoursChanged(state)) return state.sourceWindows.map((w) => ({ ...w }));
  if (!state.isUniform) {
    throw new Error('Per-day business hours cannot be rewritten from the shared start/end form');
  }
  return BUSINESS_DAYS.map((d) => d.key)
    .filter((key) => state.days.includes(key))
    .map((day) => ({ day, start: state.start, end: state.end }));
}

export function findWindowProblem(state: BusinessHoursFormState): BusinessHoursProblem | null {
  if (!state.isUniform && hasHoursChanged(state)) return 'perDayHours';
  if (!state.enabled) return null;
  if (state.days.length === 0) return 'noDays';
  if (state.start >= state.end) return 'endBeforeStart';
  return null;
}
